import { Item } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { dayjs } from "@/core/utils/date";
import { packChatPreview } from "@/payroll/pack-chat/message-preview";
import { PackChatStatusMark } from "@/payroll/pack-chat/pack-chat-status-mark";
import { referenceParts } from "@/payroll/pack-chat/reference-label";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";

/** One question in the manager's thread list. */
export const PackChatThreadRow = ({
  thread,
  selected,
  onSelect,
}: {
  thread: IPackChatThreadSummary;
  selected: boolean;
  onSelect: () => void;
}) => {
  const { label } = referenceParts(thread.context);
  const open = thread.status === "open";
  const last = thread.last_message;
  const preview = last ? packChatPreview(last) : null;

  return (
    <Item
      render={<button type="button" />}
      aria-pressed={selected}
      onClick={onSelect}
      className={cn("rounded-none px-4 py-3 text-left", {
        "bg-selected text-brand hover:bg-selected-hovered": selected,
      })}
    >
      <Stack gap={0} align="stretch" className="w-full min-w-0">
        <Stack
          className="w-full"
          direction="row"
          align="center"
          justify="between"
          gap={1}
        >
          <Text
            variant="caption"
            tone="muted"
            title={label}
            className="min-w-0 truncate font-medium tracking-widest uppercase"
          >
            {label}
          </Text>
          <span className="shrink-0">
            <PackChatStatusMark
              focusable={false}
              hasAiMessages={thread.has_ai_messages}
              needsManager={thread.needs_manager}
              responseOwner={thread.response_owner}
              open={open}
              label={open ? "Open" : "Closed"}
            />
          </span>
        </Stack>

        {last && (
          <Text
            variant="body"
            title={
              preview?.author
                ? `${preview.author}: ${preview.body}`
                : preview?.body
            }
            className={cn("line-clamp-2 pt-0.5", {
              "text-subtle": !open,
            })}
          >
            {preview?.author && (
              <span className="font-medium">{preview.author}:</span>
            )}{" "}
            {preview?.body}
          </Text>
        )}

        <Text variant="caption" tone="muted" className="pt-1">
          {dayjs(thread.updated_at).fromNow()}
        </Text>
      </Stack>
    </Item>
  );
};
